// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: the native status-bar shim
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

import { useEffect } from 'react';

type StatusBarStyle = 'light' | 'dark' | 'auto';

interface UseStatusBarOptions {
  color: string;       // hex without # prefix, e.g. '1C1A3E' or 'F0F0FF'
  style: StatusBarStyle;  // 'light' = white text/icons, 'dark' = black text/icons
}

/**
 * Sets the iOS status bar background color and text style on a per-screen basis.
 * Only runs inside the Median wrapper (not in browsers / PWAs).
 *
 * Usage in a screen component:
 *   useStatusBar({ color: '1C1A3E', style: 'light' });   // splash (charcoal bg, white text)
 *   useStatusBar({ color: 'F0F0FF', style: 'dark' });    // light screens (periwinkle bg, dark text)
 */
export function useStatusBar({ color, style }: UseStatusBarOptions) {
  useEffect(() => {
    // Guard: only execute inside Median wrapper
    const isInMedian = typeof navigator !== 'undefined' &&
                       navigator.userAgent.toLowerCase().indexOf('median') > -1;
    if (!isInMedian) return;

    const setStatusBar = () => {
      try {
        // @ts-ignore - median is injected at runtime by the wrapper
        if (typeof median !== 'undefined' && median.statusbar && median.statusbar.set) {
          // @ts-ignore
          median.statusbar.set({
            style: style,
            color: color,
            overlay: false,
          });
        } else {
          // Fallback: use median:// protocol if library not yet ready
          const params = encodeURIComponent(JSON.stringify({ style, color, overlay: false }));
          window.location.href = `median://statusbar/set?${params}`;
        }
      } catch (e) {
        // Silent fail - status bar is cosmetic, don't break the app
        console.warn('[useStatusBar] Could not set status bar:', e);
      }
    };

    setStatusBar();
    const timeout = setTimeout(setStatusBar, 100);
    return () => clearTimeout(timeout);
  }, [color, style]);
}
